import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Minus stays enabled at the minimum — there it removes the line item
    // instead of clamping (see decreaseQuantity below). value < min never
    // happens in practice (value is clamped >= min), so minus is always active.
    minusButton.disabled = value < min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * At (or below) the minimum quantity, the minus button removes the whole
   * line item — pressing "−" on the last unit dispatches a quantity:0 update,
   * which cart-items-component already treats as a line removal (with its
   * row-removal animation). Above the minimum it decrements normally.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const { quantityInput } = this.refs;
    const { value, min } = this.getCurrentValues();

    if (value <= min) {
      event.preventDefault();
      quantityInput.value = '0';
      // dispatches QuantitySelectorUpdateEvent(0, cartLine) -> line removal
      this.onQuantityChange();
      return;
    }

    super.decreaseQuantity(event);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
